import type {
  GetSettingsRequest,
  SparxOverview,
  SparxTop3Overview,
  StatementHistoryStatistics,
  StatementLeads,
  StatementStatistics,
} from "@exagens/behavioral-insight-console-client";
import type { CatalogPeriod } from "@/constants/sparxsCatalogFilters";
import type { StatementChannel } from "@/constants/statementChannel";
import {
  getSparxsStatisticApi,
  getStatementsStatisticApi,
} from "@/services/consoleApiLoaders";
import { from, type Observable, mergeMap } from "rxjs";
import { type UserProfile } from "@/hooks/useUserProfile";
import { UserProfileState } from "@/types/userProfile";
import { retryOnUnauthorized } from "@/store/consoleApi/retryOnUnauthorized";

const AUTH_HEADER_PLACEHOLDER = "bearer-set-by-middleware";

export const requestConfiguration = (
  profile: Partial<UserProfileState>,
): GetSettingsRequest => {
  const { currentFinancialInstitution } = profile;
  if (currentFinancialInstitution == null) {
    throw new Error("Profile must have currentFinancialInstitution");
  }
  return {
    financialInstitution: currentFinancialInstitution,
    authorization: AUTH_HEADER_PLACEHOLDER,
  };
};

export const getCatalogSparxsOverview = (
  profile: UserProfile,
  period: CatalogPeriod,
): Observable<SparxOverview[]> => {
  return from(getSparxsStatisticApi()).pipe(
    mergeMap((api) =>
      api.getCatalogSparxsOverview({
        ...requestConfiguration(profile),
        period,
      }),
    ),
    retryOnUnauthorized(),
  );
};

export const getCatalogSparxsTop3 = (
  profile: UserProfile,
  period: CatalogPeriod,
): Observable<SparxTop3Overview[]> => {
  return from(getSparxsStatisticApi()).pipe(
    mergeMap((api) =>
      api.getCatalogSparxsTop3({
        ...requestConfiguration(profile),
        period,
      }),
    ),
    retryOnUnauthorized(),
  );
};

export const getStatementLeads = (
  profile: UserProfile,
  period: CatalogPeriod,
): Observable<StatementLeads> => {
  return from(getStatementsStatisticApi()).pipe(
    mergeMap((api) =>
      api.getStatementLeads({
        ...requestConfiguration(profile),
        period,
      }),
    ),
    retryOnUnauthorized(),
  );
};

export const getStatementSummaries = (
  profile: UserProfile,
  channel: StatementChannel,
  period: CatalogPeriod,
): Observable<StatementStatistics> => {
  return from(getStatementsStatisticApi()).pipe(
    mergeMap((api) =>
      api.getStatementSummaries({
        ...requestConfiguration(profile),
        channel,
        period,
      }),
    ),
    retryOnUnauthorized(),
  );
};

export const getStatementHistoryStatistics = (
  profile: UserProfile,
  months: number,
): Observable<StatementHistoryStatistics[]> => {
  return from(getStatementsStatisticApi()).pipe(
    mergeMap((api) =>
      api.getStatementHistoryStatistics({
        ...requestConfiguration(profile),
        months,
      }),
    ),
    retryOnUnauthorized(),
  );
};
